import type { FeatureRule, FeatureToggle } from '@/config/types.config';
import type { DtoOrderOrderTypeEnum } from '@repo/api-types/order-retrieval';
import { type AssetType, assetTypes } from '@repo/types/asset';

import { getAppConfig } from '@/utils/window.utils';

export interface FeatureContext {
    assetType?: AssetType | undefined;
    orderType?: DtoOrderOrderTypeEnum | undefined;
    assetStatus?: string | undefined;
    permissions?: string[] | undefined;
}

const isFeatureRuleEnabled = (
    { assetType: enabledAssetType, orderTypes, statuses }: FeatureRule,
    { assetType, orderType, assetStatus }: FeatureContext
): boolean => {
    if (assetType !== enabledAssetType) {
        return false;
    }

    if (assetType === assetTypes.order && orderTypes && (!orderType || !orderTypes.includes(orderType))) {
        return false;
    }

    if (statuses && (!assetStatus || !statuses.includes(assetStatus))) {
        return false;
    }

    return true;
};

const isFeatureStaticallyEnabled = (
    featureToggle: FeatureToggle | undefined,
    featureContext: FeatureContext = {}
): boolean => {
    if (!featureToggle?.enable) {
        return false;
    }

    if (!featureToggle.enabledFor) {
        return true;
    }

    return featureToggle.enabledFor.some((rule) => isFeatureRuleEnabled(rule, featureContext));
};

const hasRequiredPermissions = (
    configPermissions: string[] | null | undefined,
    userPermissions: string[] | undefined
): boolean => {
    if (configPermissions === null || configPermissions === undefined) {
        return true;
    }

    if (configPermissions.length === 0) {
        return false;
    }

    if (!userPermissions || userPermissions.length === 0) {
        return false;
    }

    return configPermissions.every((permission) => userPermissions.includes(permission));
};

export const isFeatureEnabled = (
    featureToggle: FeatureToggle | undefined,
    featureContext: FeatureContext,
    withPermissions = true
): boolean => {
    if (!isFeatureStaticallyEnabled(featureToggle, featureContext)) {
        return false;
    }

    return !withPermissions || hasRequiredPermissions(featureToggle?.permissions, featureContext.permissions);
};

export const requiresPermissionCheck = (
    featureToggle: FeatureToggle | undefined,
    featureContext: FeatureContext = {}
): boolean => {
    return isFeatureStaticallyEnabled(featureToggle, featureContext) && Boolean(featureToggle?.permissions?.length);
};

export const isRedirectToCartOnAssetShareEnabled = (): boolean => {
    const config = getAppConfig();
    return Boolean(config.feature.redirectToCartOnAssetShare?.enable);
};

export const isPrintEnabledForDraft = (): boolean => {
    const config = getAppConfig();
    return config.shareOptions.printEnabledFor.includes(assetTypes.draft);
};
